import { useMemo } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell, LabelList } from 'recharts'
import { Button } from '@/common/ui/Form/Button/Button'
import { cn } from '@/common/lib/cn/cn'
import { getScoreBarColor } from './scoreDistributionColors'
import {
    handleChartMouseDown,
    nonInteractiveBarChartStyle,
    nonInteractiveBarProps,
    nonInteractiveChartClassName,
} from './nonInteractiveChart'

export type ScoreDistributionDatum = {
    score: number
    count: number
}

export type ScoreDistributionChartProps = {
    data: ScoreDistributionDatum[]
    totalProducts: number
    title?: string
    className?: string
    onExportPng?: () => void
    exportPngDisabled?: boolean
}

export const ScoreDistributionChart = ({
    data,
    totalProducts,
    title = 'РАСПРЕДЕЛЕНИЕ ОЦЕНОК — общий балл из 10',
    className,
    onExportPng,
    exportPngDisabled = true,
}: ScoreDistributionChartProps) => {
    const chartData = useMemo(() => data.map(item => ({ ...item, label: String(item.score) })), [data])

    const maxCount = useMemo(() => Math.max(...chartData.map(d => d.count), 1), [chartData])

    const handleExportPngClick = () => {
        if (exportPngDisabled) return
        onExportPng?.()
    }

    if (chartData.length === 0) {
        return (
            <div
                className={cn('flex min-h-[280px] items-center justify-center text-sm text-base-content/60', className)}
            >
                Нет данных для графика
            </div>
        )
    }

    return (
        <div className={cn('flex flex-col gap-4', className)}>
            <div className='flex items-start justify-between gap-4'>
                <h3 className='text-xs font-bold uppercase tracking-wide text-white'>{title}</h3>
                <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    disabled={exportPngDisabled}
                    onClick={handleExportPngClick}
                    aria-label='Скачать график в PNG'
                >
                    PNG
                </Button>
            </div>
            <p className='text-sm text-base-content/70'>
                <span className='inline-block h-2 w-2 rounded-sm bg-[#08AC81] align-middle' aria-hidden />
                <span className='ml-2'>Все товары {totalProducts} тов.</span>
            </p>
            <div className={nonInteractiveChartClassName} onMouseDown={handleChartMouseDown} role='img' aria-hidden>
                <ResponsiveContainer width='100%' height={280}>
                    <BarChart
                        data={chartData}
                        margin={{ top: 28, right: 12, left: 0, bottom: 8 }}
                        style={nonInteractiveBarChartStyle}
                    >
                        <CartesianGrid strokeDasharray='3 3' stroke='rgba(255,255,255,0.08)' vertical={false} />
                        <XAxis
                            dataKey='label'
                            stroke='#a6adbb'
                            tick={{ fill: '#a6adbb', fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            stroke='#a6adbb'
                            tick={{ fill: '#a6adbb', fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                            domain={[0, maxCount]}
                            allowDecimals={false}
                        />
                        <Bar dataKey='count' radius={[4, 4, 0, 0]} maxBarSize={48} {...nonInteractiveBarProps}>
                            {chartData.map(entry => (
                                <Cell key={entry.score} fill={getScoreBarColor(entry.score)} />
                            ))}
                            <LabelList dataKey='count' position='top' fill='#e5e7eb' fontSize={11} fontWeight={600} />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    )
}
